import api from '../api';
import type {
  EvidenceSource,
  EvidenceItem,
  EvidencePackage,
  EvidenceSourceResponse,
  EvidenceItemResponse,
  EvidencePackageResponse,
  SyncResponse,
  TestConnectionResponse,
  PackageStatusResponse,
  CreateSourceFormData,
  CreatePackageFormData,
  UploadEvidenceFormData,
  EvidenceSourceType,
  EvidenceStats
} from '../types/evidence';

class EvidenceAPI {

  // ============ EVIDENCE SOURCES ============

  async getEvidenceSources(projectId?: number): Promise<EvidenceSource[]> {
    const url = projectId
      ? `/api/evidence/sources/project/${projectId}`
      : `/api/evidence/sources`;

    const response = await api.get<EvidenceSourceResponse>(url);
    return Array.isArray(response.data.data) ? response.data.data : [response.data.data];
  }

  async getEvidenceSourceById(id: number): Promise<EvidenceSource> {
    const response = await api.get<EvidenceSourceResponse>(
      `/api/evidence/sources/${id}`
    );
    return response.data.data as EvidenceSource;
  }

  async createEvidenceSource(data: CreateSourceFormData): Promise<EvidenceSource> {
    const response = await api.post<EvidenceSourceResponse>(
      `/api/evidence/sources`,
      data
    );
    return response.data.data as EvidenceSource;
  }

  async updateEvidenceSource(id: number, data: Partial<CreateSourceFormData>): Promise<EvidenceSource> {
    const response = await api.put<EvidenceSourceResponse>(
      `/api/evidence/sources/${id}`,
      data
    );
    return response.data.data as EvidenceSource;
  }

  async deleteEvidenceSource(id: number, force: boolean = false): Promise<void> {
    await api.delete(`/api/evidence/sources/${id}${force ? '?force=true' : ''}`);
  }

  async testConnection(sourceType: EvidenceSourceType, config: Record<string, any>): Promise<TestConnectionResponse> {
    const response = await api.post<TestConnectionResponse>(
      `/api/evidence/sources/test-connection`,
      { source_type: sourceType, connection_config: config }
    );
    return response.data;
  }

  async testSourceConnection(sourceId: number): Promise<TestConnectionResponse> {
    const response = await api.post<TestConnectionResponse>(
      `/api/evidence/sources/${sourceId}/test-connection`,
      {}
    );
    return response.data;
  }

  async syncSource(sourceId: number): Promise<SyncResponse> {
    const response = await api.post<SyncResponse>(
      `/api/evidence/sources/${sourceId}/sync`,
      {}
    );
    return response.data;
  }

  async syncAllSources(projectId?: number): Promise<SyncResponse[]> {
    const sources = await this.getEvidenceSources(projectId);
    const activeSources = sources.filter(s => s.status === 'active');

    const syncPromises = activeSources.map(source => this.syncSource(source.id));
    return Promise.all(syncPromises);
  }

  // ============ EVIDENCE ITEMS ============

  async getEvidenceItems(projectId?: number, filters?: Record<string, any>): Promise<EvidenceItem[]> {
    const url = projectId
      ? `/api/evidence/items/project/${projectId}`
      : `/api/evidence/items`;

    const response = await api.get<EvidenceItemResponse>(url, {
      params: filters
    });
    return Array.isArray(response.data.data) ? response.data.data : [response.data.data];
  }

  async getEvidenceItemById(id: number): Promise<EvidenceItem> {
    const response = await api.get<EvidenceItemResponse>(
      `/api/evidence/items/${id}`
    );
    return response.data.data as EvidenceItem;
  }

  async getStaleEvidence(): Promise<EvidenceItem[]> {
    const response = await api.get<EvidenceItemResponse>(
      `/api/evidence/items/stale`
    );
    return Array.isArray(response.data.data) ? response.data.data : [];
  }

  async uploadManualEvidence(formData: FormData): Promise<EvidenceItem> {
    const response = await api.post<EvidenceItemResponse>(
      `/api/evidence/items/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data.data as EvidenceItem;
  }

  async captureScreenshot(data: {
    url: string;
    evidence_name: string;
    description?: string;
    purpose?: string;
    project_id?: number;
    source_id?: number;
    compliance_tags?: string[];
    control_id?: number;
    capture_options?: {
      full_page?: boolean;
      viewport?: { width: number; height: number };
      wait_until?: string;
      delay_ms?: number;
    };
    add_watermark?: boolean;
  }): Promise<EvidenceItem> {
    const response = await api.post<EvidenceItemResponse>(
      `/api/evidence/items/capture-screenshot`,
      data
    );
    return response.data.data as EvidenceItem;
  }

  async validateEvidence(id: number, status: string, notes?: string): Promise<EvidenceItem> {
    const response = await api.post<EvidenceItemResponse>(
      `/api/evidence/items/${id}/validate`,
      { validation_status: status, validation_notes: notes }
    );
    return response.data.data as EvidenceItem;
  }

  async downloadEvidence(id: number, evidenceName: string): Promise<void> {
    console.log('📥 Downloading evidence ID:', id);

    try {
      const response = await api.get(
        `/api/evidence/items/${id}/download`,
        {
          responseType: 'blob'
        }
      );

      console.log('✅ Response received:', {
        status: response.status,
        contentType: response.headers['content-type'],
        contentDisposition: response.headers['content-disposition'],
        dataSize: response.data?.size
      });

      // Get filename from Content-Disposition header if available
      let filename = evidenceName;
      const contentDisposition = response.headers['content-disposition'];
      console.log('📋 Content-Disposition header:', contentDisposition);

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
          console.log('✅ Filename extracted from header:', filename);
        }
      } else {
        // Fallback: If no header, assume ZIP package
        filename = `${evidenceName.replace(/[^a-z0-9]/gi, '_')}_Evidence_Package.zip`;
        console.log('⚠️ No Content-Disposition header, using fallback:', filename);
      }

      console.log('📦 Final download filename:', filename);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log('✅ Download triggered successfully');
    } catch (error: any) {
      console.error('❌ Download failed:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  async recaptureEvidence(id: number): Promise<EvidenceItem> {
    const response = await api.post<EvidenceItemResponse>(
      `/api/evidence/items/${id}/recapture`,
      {}
    );
    return response.data.data as EvidenceItem;
  }

  // ============ EVIDENCE PACKAGES ============

  async getEvidencePackages(projectId?: number, filters?: Record<string, any>): Promise<EvidencePackage[]> {
    const url = projectId
      ? `/api/evidence/packages/project/${projectId}`
      : `/api/evidence/packages`;

    const response = await api.get<EvidencePackageResponse>(url, {
      params: filters
    });
    return Array.isArray(response.data.data) ? response.data.data : [response.data.data];
  }

  async getEvidencePackageById(id: number): Promise<EvidencePackage> {
    const response = await api.get<EvidencePackageResponse>(
      `/api/evidence/packages/${id}`
    );
    return response.data.data as EvidencePackage;
  }

  async createEvidencePackage(data: CreatePackageFormData): Promise<EvidencePackage> {
    const response = await api.post<EvidencePackageResponse>(
      `/api/evidence/packages`,
      data
    );
    return response.data.data as EvidencePackage;
  }

  async getPackageStatus(id: number): Promise<PackageStatusResponse['data']> {
    const response = await api.get<PackageStatusResponse>(
      `/api/evidence/packages/${id}/status`
    );
    return response.data.data;
  }

  async downloadPackage(id: number, packageName: string): Promise<void> {
    const response = await api.get(
      `/api/evidence/packages/${id}/download`,
      {
        responseType: 'blob'
      }
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${packageName}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async sharePackage(id: number, userIds?: number[], externalEmails?: string[], accessLevel?: string): Promise<EvidencePackage> {
    const response = await api.post<EvidencePackageResponse>(
      `/api/evidence/packages/${id}/share`,
      { user_ids: userIds, external_emails: externalEmails, access_level: accessLevel }
    );
    return response.data.data as EvidencePackage;
  }

  // ============ UTILITY METHODS ============

  getSourceIcon(sourceType: EvidenceSourceType): string {
    const icons: Record<EvidenceSourceType, string> = {
      github: '🐙',
      gitlab: '🦊',
      bitbucket: '🪣',
      ssl_certificate: '🔒',
      ssl_labs: '🔐',
      aws_cloud: '☁️',
      azure_cloud: '☁️',
      gcp_cloud: '☁️',
      siem_splunk: '📊',
      siem_elastic: '🔍',
      siem_sentinel: '🛡️',
      jira: '📋',
      servicenow: '🎫',
      manual_upload: '📁'
    };
    return icons[sourceType] || '📄';
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      active: 'text-green-600 bg-green-100',
      syncing: 'text-blue-600 bg-blue-100',
      inactive: 'text-gray-600 bg-gray-100',
      error: 'text-red-600 bg-red-100',
      pending: 'text-yellow-600 bg-yellow-100',
      approved: 'text-green-600 bg-green-100',
      rejected: 'text-red-600 bg-red-100',
      needs_review: 'text-orange-600 bg-orange-100',
      current: 'text-green-600 bg-green-100',
      approaching_stale: 'text-yellow-600 bg-yellow-100',
      stale: 'text-orange-600 bg-orange-100',
      expired: 'text-red-600 bg-red-100',
      completed: 'text-green-600 bg-green-100',
      generating: 'text-blue-600 bg-blue-100',
      failed: 'text-red-600 bg-red-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  }

  formatSourceName(sourceType: EvidenceSourceType): string {
    const names: Record<EvidenceSourceType, string> = {
      github: 'GitHub',
      gitlab: 'GitLab',
      bitbucket: 'Bitbucket',
      ssl_certificate: 'SSL Certificate',
      ssl_labs: 'SSL Labs',
      aws_cloud: 'AWS Cloud',
      azure_cloud: 'Azure Cloud',
      gcp_cloud: 'GCP Cloud',
      siem_splunk: 'Splunk SIEM',
      siem_elastic: 'Elasticsearch',
      siem_sentinel: 'Microsoft Sentinel',
      jira: 'Jira',
      servicenow: 'ServiceNow',
      manual_upload: 'Manual Upload'
    };
    return names[sourceType] || sourceType;
  }

  async getEvidenceStats(projectId?: number): Promise<EvidenceStats> {
    const [sources, items] = await Promise.all([
      this.getEvidenceSources(projectId),
      this.getEvidenceItems(projectId)
    ]);

    const activeSources = sources.filter(s => s.status === 'active').length;
    const syncingSources = sources.filter(s => s.status === 'syncing').length;
    const validatedEvidence = items.filter(i => i.validation_status === 'approved').length;
    const staleEvidence = items.filter(i => i.is_stale).length;

    // Group by type
    const byType: Record<string, number> = {};
    items.forEach(item => {
      byType[item.evidence_type] = (byType[item.evidence_type] || 0) + 1;
    });

    // Group by framework
    const byFramework: Record<string, number> = {};
    items.forEach(item => {
      item.regulatory_frameworks?.forEach(framework => {
        byFramework[framework] = (byFramework[framework] || 0) + 1;
      });
    });

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentCollections = items.filter(
      i => new Date(i.collection_date) > thirtyDaysAgo
    ).length;

    return {
      total_sources: sources.length,
      active_sources: activeSources,
      syncing_sources: syncingSources,
      total_evidence: items.length,
      validated_evidence: validatedEvidence,
      stale_evidence: staleEvidence,
      recent_collections: recentCollections,
      by_type: byType,
      by_framework: byFramework
    };
  }
}

export default new EvidenceAPI();
